import React, { useState } from 'react';
import './LoginModal.css';
import { useTranslation } from '../contexts/TranslationContext';
import { NOTIFICATION_MESSAGES, NotificationType } from '../constants/notifications';
import {
  buildMsisdn,
  COUNTRY_CODE,
  isValidLocalPhoneInput,
  PHONE_INPUT_MAX_LENGTH,
  sanitizeLocalPhoneInput,
} from '../constants/phone';
import { sendOtp } from '../services/authService';
import { SendOtpResponse } from '../types/auth';

interface LoginModalProps {
  onSubmit: (msisdn: string, otpResponse: SendOtpResponse) => void;
  onNotify: (message: string, type: NotificationType) => void;
  onClose: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ onSubmit, onNotify, onClose }) => {
  const { t } = useTranslation();
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(sanitizeLocalPhoneInput(e.target.value));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isValidLocalPhoneInput(phone)) {
      return;
    }

    const msisdn = buildMsisdn(phone);
    setIsLoading(true);

    try {
      const response = await sendOtp(msisdn);

      if (response.ok) {
        onSubmit(msisdn, response);
      } else {
        onNotify(NOTIFICATION_MESSAGES.ERROR_GENERIC, 'error');
      }
    } catch {
      onNotify(NOTIFICATION_MESSAGES.ERROR_GENERIC, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const isSubmitDisabled = !isValidLocalPhoneInput(phone) || isLoading;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose} type="button">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>

        <div className="modal-header">
          <div className="modal-logo-custom">
            <div className="modal-play-icon">
              <svg viewBox="0 0 100 100" className="modal-play-svg">
                <defs>
                  <linearGradient id="modalSnapflixGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#dc2626" />
                    <stop offset="50%" stopColor="#f97316" />
                    <stop offset="100%" stopColor="#fbbf24" />
                  </linearGradient>
                </defs>
                <circle cx="50" cy="50" r="45" fill="url(#modalSnapflixGradient)" className="modal-play-circle" />
                <path d="M 40 30 L 40 70 L 65 50 Z" fill="white" className="modal-play-triangle" />
                <circle cx="50" cy="50" r="45" fill="none" stroke="rgba(255,255, 255, 0.3)" strokeWidth="2" className="modal-play-ring" />
              </svg>
            </div>
            <div className="modal-logo-text">
              <span className="modal-text-snap" style={{ color: '#f97316', backgroundColor: 'transparent' }}>SNAP</span>
              <span className="modal-text-flix" style={{ color: '#fbbf24', backgroundColor: 'transparent' }}>FLIX</span>
            </div>
          </div>
          <h1 className="modal-title">{t('login.welcome')}</h1>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="input-group">
            <label className="input-label">{t('login.phone.label')}</label>
            <div className="phone-input-wrapper">
              <span className="phone-prefix">+{COUNTRY_CODE}</span>
              <input
                type="tel"
                inputMode="numeric"
                value={phone}
                onChange={handlePhoneChange}
                className="phone-input"
                placeholder="76521776"
                maxLength={PHONE_INPUT_MAX_LENGTH}
                disabled={isLoading}
                autoComplete="tel-national"
              />
            </div>
          </div>

          <button
            type="submit"
            className={`send-otp-button ${isLoading ? 'loading' : ''}`}
            disabled={isSubmitDisabled}
            style={{
              background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
              opacity: isSubmitDisabled ? 0.6 : 1,
            }}
          >
            {isLoading ? (
              <>
                <span className="button-spinner" aria-hidden="true" />
                <span>Sending OTP...</span>
              </>
            ) : (
              <>
                <span>{t('login.send.otp')}</span>
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                  <path d="M4 10L16 10M10 4L16 10L10 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </>
            )}
          </button>
        </form>

        <div className="security-notice">
          <div className="security-icon">🔒</div>
          <span>{t('login.security')}</span>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
